/**
 Copyright Highway9 Networks Inc.
 */
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import { authenticationService, operationService, supportChannelServices, systemService } from "../../services";
import { SupportChannel } from "../../types/SupportChannel";
import { DEFAULT_LANDING_PAGE_ENUM, System } from "../../types/systemSetting";
import { Service } from "~/types/Service";
import { OperationDetails } from "~/types/operations";

type SettingsState = {
  system: System[];
  services: Service[];
  support: SupportChannel;
  operations: OperationDetails[];
  loading: boolean;
  systemSettingsLoading: boolean;
  error: string | null;
};

const initialState: SettingsState = {
  system: [],
  services: [],
  operations: [],
  support: {
    enabled: false,
    id: "",
    state: "INACTIVE",
  },
  loading: true,
  systemSettingsLoading: true,
  error: null,
};

export const fetchSystemSettings = createAsyncThunk("settings/fetchSystemSettings", async () => {
  const response = await systemService.getSystemSettingsData();
  return response;
});

export const fetchSupportChannel = createAsyncThunk("settings/fetchSupportChannel", async () => {
  const result = await supportChannelServices.getSupportChannel();
  return result;
});

export const fetchSystemServices = createAsyncThunk("settings/fetchServices", async () => {
  const response = await systemService.getServices();
  return response;
});

export const fetchOperations = createAsyncThunk("settings/fetchOperations", async (status?: string) => {
  const response = await operationService.getAllOperations({
    status: status ?? undefined
  });
  return response;
});



const SettingsSlice = createSlice({
  name: "settings",
  initialState,
  reducers: {
    setSystemSettings(state, action: PayloadAction<System[]>) {
      state.system = action.payload;
    },
    setSupportSettings(state, action: PayloadAction<SupportChannel>) {
      state.support = action.payload;
    },
    setSupportSettingsEnabled(state, action: PayloadAction<boolean>) {
      state.support.enabled = action.payload;
    },
    // to worker-patched list without going through fetchOperations.fulfilled
    patchOperations(state, action: PayloadAction<OperationDetails[]>) {
      state.operations = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemSettings.fulfilled, (state, action) => {
        state.system = action.payload;
        state.systemSettingsLoading = false;
      })
      .addCase(fetchSystemSettings.pending, (state) => {
        state.systemSettingsLoading = true;
      })
      .addCase(fetchSystemSettings.rejected, (state, action) => {
        state.systemSettingsLoading = false;
        state.error = action.error.message ?? "Something went wrong";
      })

      // Support Channel Fetch
      .addCase(fetchSupportChannel.fulfilled, (state, action) => {
        state.support = action.payload[0];
        state.loading = false;
      })
      .addCase(fetchSupportChannel.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSupportChannel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Something went wrong";
      })

      // VMC service Fetch
      .addCase(fetchSystemServices.fulfilled, (state, action) => {
        state.services = action.payload;
        state.loading = false;
      })
      .addCase(fetchSystemServices.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSystemServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? "Something went wrong";
      })

      // Operations Fetch
      .addCase(fetchOperations.fulfilled, (state, action) => {
        state.operations = action.payload;
      });
  },
});

export const settingsActions = SettingsSlice.actions;
export default SettingsSlice.reducer;

export const settingsSystem = (state: RootState) => state.settings.system;
export const settingsOperations = (status?: string) => (state: RootState) => {
  if (status) {
    return state.settings.operations.filter((operation) => operation.status === status);
  }
  return state.settings.operations;
};
export const settingsSupport = (state: RootState) => state.settings.support;
export const settingsSupportEnabled = (state: RootState) => state.settings.support.enabled;
export const settingsSystemDashboardConsoleEnabled = (state: RootState) => state.settings.system.some((item) => item.name === "dashboard.console.enabled" && item.value);
export const settingsSystemOnboardingEnabled = (state: RootState) => state.settings.system.some((item) => item.name === "dashboard.getting.started.enabled" && item.value);
export const settingsSystemNewWizardUiEnabled = (state: RootState) => state.settings.system.some((item) => item.name === "getting.started.new.wizard.ui.enabled" && item.value);
export const settingsAssistantEnabled = (state: RootState) => authenticationService.isOperator() || state.settings.system.some((item) => item.name === "system.chatbot.enabled" && item.value)
export const settingsAirgapEnabled = (state: RootState) => state.settings.system.some((item) => item.name === "airgap.enabled" && item.value);
export const settingsDashboardInsightsEnabled = (state: RootState) => authenticationService.isOperator() || state.settings.system.some((item) => item.name === "dashboard.insights.enabled" && item.value)
export const settingsChannelPlanInsightsEnabled = (state: RootState) => state.settings.system.some((item) => item.name === "channelPlan.insights.enabled" && item.value);
export const settingsLoading = (state: RootState) => state.settings.systemSettingsLoading;
export const settingsDefaultLandingPage = (state: RootState): DEFAULT_LANDING_PAGE_ENUM => state.settings.system.find((item) => item.name === "default.landing.page")?.value;

export const settingsDeviceInsightsEnabled = (state: RootState) => state.settings.system.some((item) => item.name === "deviceInfo.insights.enabled" && item.value);

export const settingsDeviceTimelineCoreEventsEnabled = (state: RootState) => state.settings.system.some((item) => item.name === "device.timeline.coreevents.enabled" && item.value);

export const settingsDeviceConnectionQualityEnabled = (state: RootState) => state.settings.system.some((item) => item.name === "device.metric.connectionQuality.enabled" && item.value);
export const settingsServices = (state: RootState) => state.settings.services;

export const settingsServiceAPP_INSIGHTEnabled = (state: RootState) => state.settings.services.some((service) => service.serviceType === "APP_INSIGHT");
export const settingsServiceClassMEnabled = (state: RootState) => state.settings.services.some((service) => service.serviceType === "CLASS_M");
export const settingsRadioMapGroupingEnabled = (state: RootState) => state.settings.system.some((item) => item.name === "radio.indoor.map.grouping.enabled" && item.value);
export const settingsOutdoorGroupFloorPlanEnabled = (state: RootState) => state.settings.system.some((item) => item.name === "radio.outdoor.group.floorplan.enabled" && item.value);
export const settingsRadioWalkTestEnabled = (state: RootState) => state.settings.system.some((item) => item.name === "radio.walktest.enabled" && item.value);

// Operators always have access; all tenant roles need radio.monitoring.enabled.
export const settingsRadioMonitoringEnabled = (state: RootState) =>
  authenticationService.isOperator() ||
  state.settings.system.some((item) => item.name === "radio.monitoring.enabled" && item.value);

export const settingsRadioNeighborAutoAssignDefault = (state: RootState) => state.settings.system.some((item) => item.name === "radio.neighbor.autoassign.default" && item.value);
export const settingsAnalysedMetricsEnabled = (state: RootState) => state.settings.system.some((item) => item.name === "kpi.analysed.metrics.enabled" && item.value);
export const settingsEdgeNodeInfoReportingEnabled = (state: RootState) => state.settings.system.some((item) => item.name === "edge.nodeInfo.reporting.enabled" && item.value);
